import { msg } from '@lingui/core/macro';
import { Trans, Plural } from '@lingui/react/macro';
import type { I18n, MessageDescriptor } from '@lingui/core';
import { useLingui } from '@lingui/react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Area, AreaChart, Tooltip, XAxis } from 'recharts';
import { getExecutionSummary, getTaskExecutionSummary, listChannelExecutions, listTaskExecutions, type ExecutionListEntry, type ExecutionSummary, type TaskUsageSummary } from '../lib/api';
import { subscribeExecutionUsageUpdates } from '../lib/execution-usage-updates';
import { formatDate, formatNumber } from '../i18n/format';

type Scope = 'channel' | 'thread' | 'task';

const SCOPE_TITLE: Record<Scope, MessageDescriptor> = {
  channel: msg`Token usage for this channel and threads you can access`,
  thread: msg`Token usage for this thread`,
  task: msg`Token usage for this task thread`,
};

function formatTokens(i18n: I18n, tokens: number): string {
  return formatNumber(i18n, tokens, { notation: 'compact', maximumFractionDigits: 1 });
}

function modelTokens(entry: ExecutionSummary['models'][number]): number {
  return entry.input_tokens + entry.output_tokens + entry.cache_read_tokens + entry.cache_write_tokens;
}

/** A compact user-facing token summary; lifecycle diagnostics stay opt-in. */
export function ExecutionUsageChip({ resourceId, subscriptionId = resourceId, scope = 'channel', deferred = false, initialSummary }: { resourceId: string; subscriptionId?: string; scope?: Scope; deferred?: boolean; initialSummary?: TaskUsageSummary }) {
  const { i18n } = useLingui();
  const scopeTitle = i18n._(SCOPE_TITLE[scope]);
  const [summary, setSummary] = useState<ExecutionSummary | TaskUsageSummary | null>(initialSummary ?? null);
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [executions, setExecutions] = useState<ExecutionListEntry[]>([]);
  const [executionListLoaded, setExecutionListLoaded] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLSpanElement>(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, right: 8 });

  const refresh = useCallback(() => {
    void (scope === 'task' ? getTaskExecutionSummary(resourceId) : getExecutionSummary(resourceId))
      .then(setSummary)
      .catch(() => {});
  }, [resourceId, scope]);

  useEffect(() => {
    setExecutions([]);
    setExecutionListLoaded(false);
    setSummary(initialSummary ?? null);
    // List responses already include a server-batched task rollup. Do not
    // replace it with one request per visible row on mount.
    if (!deferred && !initialSummary) refresh();
  }, [deferred, initialSummary, resourceId, refresh]);
  const loadExecutions = useCallback(() => {
    void (async () => {
      const all: ExecutionListEntry[] = [];
      let before: string | undefined;
      do {
        const page = await (scope === 'task' ? listTaskExecutions(resourceId, { limit: 200, before }) : listChannelExecutions(resourceId, { limit: 200, before }));
        all.push(...page.executions);
        before = page.has_more ? page.next_cursor : undefined;
      } while (before);
      setExecutions(all);
      setExecutionListLoaded(true);
    })().catch(() => setExecutionListLoaded(true));
  }, [resourceId, scope]);
  useEffect(() => { if (open && !executionListLoaded) loadExecutions(); }, [executionListLoaded, loadExecutions, open]);
  useEffect(() => {
    if (!subscriptionId) return;
    return subscribeExecutionUsageUpdates((id) => { if (id === subscriptionId) refresh(); });
  }, [subscriptionId, refresh]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !popoverRef.current?.contains(target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside, true); document.removeEventListener('keydown', escape); };
  }, [open]);
  useLayoutEffect(() => {
    if (!open) return;
    const positionPopover = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPopoverPosition({ top: rect.bottom + 6, right: Math.max(8, window.innerWidth - rect.right) });
    };
    positionPopover();
    window.addEventListener('resize', positionPopover);
    window.addEventListener('scroll', positionPopover, true);
    return () => {
      window.removeEventListener('resize', positionPopover);
      window.removeEventListener('scroll', positionPopover, true);
    };
  }, [open]);

  if (!summary || summary.coverage === 'unavailable') {
    // A task board may contain hundreds of rows. Do not fan out one summary
    // request per row merely to decide whether a usage chip is visible.
    return deferred ? <button type="button" className="execution-usage-chip" title={scopeTitle}
      onClick={() => { setOpen(true); refresh(); }} aria-label={i18n._(msg`${scopeTitle}. View token usage.`)}><Trans>Usage</Trans></button> : null;
  }

  const total = summary.input_tokens + summary.output_tokens + summary.cache_read_tokens + summary.cache_write_tokens;
  const parts = [
    [i18n._(msg`Input`), summary.input_tokens, '#6366f1'],
    [i18n._(msg`Output`), summary.output_tokens, '#22c55e'],
    [i18n._(msg`Cache`), summary.cache_read_tokens + summary.cache_write_tokens, '#f59e0b'],
  ] as const;
  const models = [...(('models' in summary ? summary.models : []) ?? [])].sort((a, b) => modelTokens(b) - modelTokens(a)).slice(0, 3);
  const label = i18n._(msg`${formatTokens(i18n, total)} tok`);

  return <span ref={rootRef} style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
    <button type="button" className="execution-usage-chip" title={scopeTitle}
      onClick={(event) => { event.stopPropagation(); setOpen(value => !value); setShowDetails(false); refresh(); }}
      aria-expanded={open} aria-label={i18n._(msg`${scopeTitle}. ${label}.`)}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border-subtle, #e4e4e7)', background: 'var(--bg-tertiary, rgba(127,127,127,0.08))', color: 'var(--text-secondary, #71717a)', fontSize: 12, fontWeight: 500, lineHeight: 1.2, whiteSpace: 'nowrap', cursor: 'pointer' }}>
      {label}
    </button>
    {open && createPortal(<span ref={popoverRef} role="dialog" aria-label={i18n._(msg`Token usage`)} style={{ position: 'fixed', zIndex: 10_000, top: popoverPosition.top, right: popoverPosition.right, width: 320, maxWidth: 'calc(100vw - 16px)', padding: 12, borderRadius: 8, border: '1px solid var(--border-subtle, #e4e4e7)', background: 'var(--bg-floating, var(--bg-secondary, #fff))', boxShadow: '0 8px 24px rgba(0,0,0,.18)', color: 'var(--text-primary, #18181b)' }}>
      <strong style={{ display: 'block', fontSize: 12 }}><Trans>Token usage</Trans></strong>
      <span style={{ display: 'block', marginTop: 2, color: 'var(--text-secondary, #71717a)', fontSize: 11 }}>{scope === 'channel' ? i18n._(msg`Channel rollup`) : scope === 'task' ? i18n._(msg`This task thread`) : i18n._(msg`This thread`)}</span>
      <span aria-label={i18n._(msg`Token breakdown: ${formatTokens(i18n, total)} total`)} style={{ display: 'flex', height: 8, overflow: 'hidden', borderRadius: 99, background: 'var(--border-subtle, #e4e4e7)', marginTop: 12 }}>
        {parts.map(([name, value, color]) => value > 0 && <span key={name} title={i18n._(msg`${name}: ${formatTokens(i18n, value)}`)} style={{ width: `${total ? (value / total) * 100 : 0}%`, background: color }} />)}
      </span>
      <span style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px 14px', marginTop: 9, fontSize: 11 }}>
        {parts.map(([name, value, color]) => <span key={name} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span><i style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: color, marginRight: 5 }} />{name}</span><span>{formatTokens(i18n, value)}</span></span>)}
        <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontWeight: 600 }}><span><Trans>Total</Trans></span><span>{formatTokens(i18n, total)}</span></span>
      </span>
      {models.length > 0 && <span style={{ display: 'block', borderTop: '1px solid var(--border-subtle, #e4e4e7)', marginTop: 11, paddingTop: 9 }}><strong style={{ fontSize: 11 }}><Trans>Top models</Trans></strong>{models.map(entry => <span key={`${entry.provider}\u0000${entry.model}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 5, fontSize: 11 }}><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.provider} · {entry.model}</span><span>{formatTokens(i18n, modelTokens(entry))}</span></span>)}</span>}
      <ExecutionChart executions={executions} loaded={executionListLoaded} />
      <button type="button" className="btn btn-sm" onClick={() => setShowDetails(value => !value)} aria-expanded={showDetails} style={{ marginTop: 11, fontSize: 11 }}>
        {showDetails ? i18n._(msg`Hide execution details`) : i18n._(msg`View lifecycle details`)}
      </button>
      {showDetails && <span style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px 14px', marginTop: 9, paddingTop: 9, borderTop: '1px solid var(--border-subtle, #e4e4e7)', fontSize: 11 }}>
        <Metric label={i18n._(msg`Executions`)} value={summary.execution_count} />
        <Metric label={i18n._(msg`Reports`)} value={summary.usage_report_count} />
        <Metric label={i18n._(msg`Started`)} value={summary.started_count} />
        <Metric label={i18n._(msg`Finished`)} value={summary.finished_count} />
        <Metric label={i18n._(msg`Abandoned`)} value={summary.abandoned_count} />
        <span style={{ color: 'var(--text-secondary, #71717a)' }}>{summary.coverage === 'complete' ? i18n._(msg`Lifecycle complete`) : i18n._(msg`Lifecycle partial`)}</span>
      </span>}
    </span>, document.body)}
  </span>;
}

function executionTokens(execution: ExecutionListEntry): number {
  return execution.input_tokens + execution.output_tokens + execution.cache_read_tokens + execution.cache_write_tokens;
}

type ExecutionChartPoint = {
  execution: ExecutionListEntry;
  index: number;
  label: string;
  timestamp: number;
  tokens: number;
};

function ExecutionTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: ExecutionChartPoint }> }) {
  const { i18n } = useLingui();
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  const { execution, index, tokens } = point;
  const status = !execution.terminal_kind ? i18n._(msg`In progress`) : execution.terminal_kind === 'abandoned' ? i18n._(msg`Abandoned`) : execution.succeeded ? i18n._(msg`Succeeded`) : i18n._(msg`Failed`);
  return <div role="tooltip" style={{ minWidth: 180, padding: 8, border: '1px solid var(--border-subtle, #e4e4e7)', borderRadius: 6, background: 'var(--bg-floating, #fff)', boxShadow: '0 4px 14px rgba(0,0,0,.15)', color: 'var(--text-primary, #18181b)', fontSize: 11 }}>
    <strong><Trans>Execution {formatNumber(i18n, index + 1)}</Trans></strong>
    <div style={{ color: 'var(--text-secondary, #71717a)', marginTop: 2 }}>{formatDate(i18n, point.timestamp, { dateStyle: 'medium', timeStyle: 'short' })}</div>
    <div style={{ marginTop: 6 }}><Trans>Total: {formatTokens(i18n, tokens)} tokens</Trans></div>
    <div><Trans>Input: {formatTokens(i18n, execution.input_tokens)} · Output: {formatTokens(i18n, execution.output_tokens)}</Trans></div>
    <div><Trans>Cache: {formatTokens(i18n, execution.cache_read_tokens + execution.cache_write_tokens)}</Trans></div>
    <div><Trans>Status: {status}</Trans></div>
  </div>;
}

function ExecutionChart({ executions, loaded }: { executions: ExecutionListEntry[]; loaded: boolean }) {
  const { i18n } = useLingui();
  // The API is newest-first for cursor pagination; a trend reads left-to-right.
  const ordered = [...executions].reverse();
  const points: ExecutionChartPoint[] = ordered.map((execution, index) => {
    const timestamp = execution.started_at ?? execution.admitted_at;
    return { execution, index, timestamp, tokens: executionTokens(execution), label: formatDate(i18n, timestamp, { hour: '2-digit', minute: '2-digit' }) };
  });
  return <span style={{ display: 'block', borderTop: '1px solid var(--border-subtle, #e4e4e7)', marginTop: 11, paddingTop: 9 }}>
    <strong style={{ fontSize: 11 }}><Trans>All executions</Trans></strong>
    {!loaded && <span style={{ display: 'block', marginTop: 5, color: 'var(--text-secondary, #71717a)' }}><Trans>Loading executions…</Trans></span>}
    {loaded && points.length === 0 && <span style={{ display: 'block', marginTop: 5, color: 'var(--text-secondary, #71717a)' }}><Trans>No executions to chart.</Trans></span>}
    {points.length > 0 && <AreaChart width={296} height={108} data={points} margin={{ top: 8, right: 6, bottom: 0, left: 6 }} role="img" aria-label={i18n._(msg`Execution token trend, oldest to newest`)}>
      <defs><linearGradient id="execution-usage-area" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#818cf8" stopOpacity={0.42} /><stop offset="100%" stopColor="#818cf8" stopOpacity={0.03} /></linearGradient></defs>
      <XAxis dataKey="label" tick={{ fill: 'var(--text-secondary, #71717a)', fontSize: 10 }} tickLine={false} axisLine={{ stroke: 'var(--border-subtle, #e4e4e7)' }} interval="preserveStartEnd" />
      <Tooltip content={<ExecutionTooltip />} cursor={{ stroke: '#818cf8', strokeWidth: 1 }} />
      <Area type="monotone" dataKey="tokens" stroke="#818cf8" strokeWidth={2.5} fill="url(#execution-usage-area)" dot={false} activeDot={{ r: 5 }} isAnimationActive={false} />
    </AreaChart>}
    {ordered.length > 0 && <span style={{ display: 'flex', justifyContent: 'space-between', marginTop: 1, color: 'var(--text-secondary, #71717a)', fontSize: 10 }}><span>{formatDate(i18n, ordered[0].started_at ?? ordered[0].admitted_at, { dateStyle: 'medium', timeStyle: 'short' })}</span><span>{formatDate(i18n, ordered.at(-1)!.started_at ?? ordered.at(-1)!.admitted_at, { dateStyle: 'medium', timeStyle: 'short' })} · <Plural value={ordered.length} one="# execution total" other="# executions total" /></span></span>}
  </span>;
}

function Metric({ label, value }: { label: string; value: number }) {
  const { i18n } = useLingui();
  return <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><span style={{ color: 'var(--text-secondary, #71717a)' }}>{label}</span><span>{formatNumber(i18n, value)}</span></span>;
}
